import { useState } from 'react';
import { BnplFeePayer } from '@prisma/client';
import { toast } from 'react-toastify';
import SettingsSection from '../SettingsSection';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  UpdatePaymentsRatesPayload,
  updatePaymentsRatesSchema,
} from '../../../types/businesses';
import { noop, roundToMaxDecimalPlaces } from '../../../utils/global/helper';
import { BNPL_FEE_RATE } from '../../../constants/payments';
import useUpdatePaymentsRates from '../../../hooks/businesses/useUpdatePaymentsRates';
import SectionDivider from '../SectionDivider';
import {
  DEFAULT_BACKEND_PAYMENTS_RATE,
  ROUTES,
} from '../../../constants/global';
import useBusinessCapabilities from '../../../hooks/businesses/useBusinessCapabilities';

function PaymentsRatesSettings() {
  const { user, reloadUser } = useUser<LoadedUser>();

  const [form, setForm] = useState<FormData | null>(null);

  const customerRate = user?.business?.onlineCustomerServiceFeeRate ?? 0;
  const businessRate =
    user?.business?.paymentsRate ?? DEFAULT_BACKEND_PAYMENTS_RATE;
  const totalRate = customerRate + businessRate;

  const onSuccess = async () => {
    await reloadUser();
    toast.success('Changes saved!');
    setForm(null);
  };

  const { mutateAsync: updatePaymentsRates } = useUpdatePaymentsRates({
    onSuccess,
    onError: () => {
      toast.error('Failed to update');
    },
  });

  const { data: capabilities } = useBusinessCapabilities();

  const perChargeFeeEnabled = businessRate > 0;
  const perChargeFeeText = ' + $0.30 per charge';

  const paymentsRatesForm: FormData<UpdatePaymentsRatesPayload> = {
    initialValues: {
      customerRate: customerRate * 100,
      businessRate: businessRate * 100,
      isBnplEnabled: user?.business?.isBnplEnabled ?? false,
      bnplFeePayer: user?.business?.bnplFeePayer ?? BnplFeePayer.CUSTOMER,
    },
    title: 'Edit Payment Rates',
    inputRows: [
      [
        {
          inputType: 'dual-sliders',
          names: ['businessRate', 'customerRate'],
          labels: ['You Cover', 'Customer Covers'],
          min: 0,
          max: Math.round(totalRate * 1000) / 10,
          step: 0.1,
          valueFormatter: val => `${val}%`,
          name: ['businessRate', 'customerRate'],
        },
      ],
    ],
    validationSchema: updatePaymentsRatesSchema,
    onSubmit: async values => {
      const body = await updatePaymentsRatesSchema
        .noUnknown()
        .validate(values, {
          stripUnknown: true,
        });
      const payload: UpdatePaymentsRatesPayload = {
        customerRate: roundToMaxDecimalPlaces(
          (body.customerRate ?? 0) / 100,
          3,
        ),
        businessRate: roundToMaxDecimalPlaces(
          (body.businessRate ?? 0) / 100,
          3,
        ),
      };
      await updatePaymentsRates(payload).catch(noop);
    },
  };

  const bnplForm: FormData<UpdatePaymentsRatesPayload> = {
    initialValues: {
      isBnplEnabled: user?.business?.isBnplEnabled ?? false,
      bnplFeePayer: user?.business?.bnplFeePayer ?? BnplFeePayer.CUSTOMER,
    },
    title: 'BNPL',
    inputRows: [
      [
        {
          label: 'Fee Payer',
          inputType: 'select',
          name: 'bnplFeePayer',
          options: Object.values(BnplFeePayer).map(feePayer => ({
            label:
              feePayer === 'BUSINESS'
                ? `You pay excess ${BNPL_FEE_RATE * 100}%`
                : `Customer pays excess ${BNPL_FEE_RATE * 100}%`,
            value: feePayer,
          })),
        },
      ],
    ],
    validationSchema: updatePaymentsRatesSchema,
    onSubmit: async values => {
      const body = await updatePaymentsRatesSchema
        .noUnknown()
        .validate(values, {
          stripUnknown: true,
        });
      const payload: UpdatePaymentsRatesPayload = {
        isBnplEnabled: body.isBnplEnabled ?? false,
        bnplFeePayer: body.bnplFeePayer ?? BnplFeePayer.CUSTOMER,
      };
      updatePaymentsRates(payload);
    },
    submitButtonText: 'Save',
  };

  return (
    <>
      <SettingsSection
        title="Payments Rate"
        rows={[
          {
            title: 'Total Payments Rate',
            label: `${roundToMaxDecimalPlaces(
              (customerRate + businessRate) * 100,
              1,
            )}%${perChargeFeeEnabled ? perChargeFeeText : ''}`,
          },
          {
            title: 'You Cover',
            label: `${roundToMaxDecimalPlaces(businessRate * 100, 1)}%${
              perChargeFeeEnabled ? perChargeFeeText : ''
            }`,
            tooltip: 'The percentage of the total payments rate that you cover',
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => {
                setForm(paymentsRatesForm);
              },
            },
          },
          {
            title: 'Customer Covers',
            label: `${roundToMaxDecimalPlaces(customerRate * 100, 1)}%`,
            tooltip:
              'The percentage of the total payments rate that the customer covers',
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => {
                setForm(paymentsRatesForm);
              },
            },
          },
        ]}
      />

      <SectionDivider />

      <SettingsSection
        title="Buy Now, Pay Later (BNPL)"
        alert={
          capabilities &&
          (!capabilities.affirm.active || !capabilities.afterpay.active)
            ? {
                type: 'warning',
                title: 'Some BNPL services are not available',
                subtitle:
                  'Please head to your payments account to complete the requirements and enable these services.',
                dismissible: false,
                button: {
                  text: 'View Payments Account',
                  href: ROUTES.payments,
                },
              }
            : undefined
        }
        rows={[
          {
            title: 'Enable BNPL',
            label: `Additional ${BNPL_FEE_RATE * 100}% for BNPL payments`,
            action: {
              type: 'switch',
              checked: !!user?.business?.isBnplEnabled,
              onChange: async (checked: boolean) => {
                updatePaymentsRates({
                  isBnplEnabled: checked,
                });
              },
            },
          },
          {
            title: 'Fee Payer',
            label: `${
              user?.business?.bnplFeePayer === 'BUSINESS'
                ? `You pay excess ${BNPL_FEE_RATE * 100}%`
                : `Customer pays excess ${BNPL_FEE_RATE * 100}%`
            }`,
            tooltip:
              'Whether the business of the business will pay the additional BNPL fee',
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => {
                setForm(bnplForm);
              },
            },
          },
        ]}
      />

      {form && (
        <FormSmallModal
          form={form}
          onClose={() => setForm(null)}
          open={!!form}
        />
      )}
    </>
  );
}

export default PaymentsRatesSettings;
